/**
 * @file routes/_app/analytics/index.tsx
 * @description 統計分析總覽頁。
 *
 * 提供系統頂層 KPI 卡片 + 案件狀態分布圖表。
 * 使用 recharts 的 PieChart（客戶分布）和 BarChart（案件趨勢）。
 * 匯出按鈕呼叫 /api/analytics/export?type=overview 觸發 Excel 下載。
 *
 * 視覺：PageShell + StatCard + SectionCard + EmptyState
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { Button, Spin, Space } from 'antd';
import { message } from '@/lib/antd-app';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  FileDown,
  RefreshCw,
  AlertTriangle,
  ClipboardCheck,
  ShieldCheck,
  Clock,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useOverview, useCaseTrend, useClientStats } from '@/hooks/useAnalytics';
import { PageShell, PageMetaItem, SectionCard, StatCard, EmptyState } from '@/components/shell';
import { color, space } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/analytics/')({ component: AnalyticsOverviewPage });

/* ── 圖表顏色 ── */
const CHART_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#e11d48', '#7c3aed', '#db2777'];

/** 統計分析總覽頁 */
function AnalyticsOverviewPage() {
  const [exporting, setExporting] = useState(false);

  /* ── 資料查詢 ── */
  const overview = useOverview();
  const caseTrend = useCaseTrend({}, 'month');
  const clientStats = useClientStats();

  /* ── Excel 匯出 ── */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const resp = await fetch('/api/analytics/export?type=overview', {
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zhcb-analytics-overview-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('匯出成功');
    } catch (err) {
      message.error('匯出失敗');
      console.error('analytics export failed', err);
    } finally {
      setExporting(false);
    }
  }, []);

  /* ── 刷新 ── */
  const handleRefresh = useCallback(() => {
    void overview.refetch();
    void caseTrend.refetch();
    void clientStats.refetch();
  }, [overview, caseTrend, clientStats]);

  const data = overview.data?.overview;
  const trendData = caseTrend.data?.trend ?? [];
  const verificationDist = clientStats.data?.verificationStatusDist ?? [];

  return (
    <PageShell
      title="統計分析"
      description="系統關鍵業務指標與趨勢總覽"
      meta={[<PageMetaItem key="updated" label="數據更新" value="即時" />]}
      actions={
        <Space>
          <Button
            icon={<RefreshCw size={14} />}
            onClick={handleRefresh}
            loading={overview.isFetching}
          >
            刷新
          </Button>
          <Button icon={<FileDown size={14} />} loading={exporting} onClick={handleExport}>
            匯出 Excel
          </Button>
        </Space>
      }
    >
      {/* KPI 卡片區 */}
      {overview.isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: space[4],
              marginBottom: space[6],
            }}
          >
            <StatCard
              title="待處理案件"
              value={data?.pendingCases ?? 0}
              suffix="件"
              tone="warning"
              icon={<Clock size={18} />}
            />
            <StatCard
              title="進行中稽核"
              value={data?.activeAuditTasks ?? 0}
              suffix="件"
              tone="info"
              icon={<ClipboardCheck size={18} />}
            />
            <StatCard
              title="本月新發證書"
              value={data?.monthlyIssuedCerts ?? 0}
              suffix="張"
              tone="success"
              icon={<ShieldCheck size={18} />}
            />
            <StatCard
              title="即將到期（30天）"
              value={data?.expiringCertsSoon ?? 0}
              suffix="張"
              tone={data?.expiringCertsSoon ? 'warning' : 'neutral'}
              icon={<AlertTriangle size={18} />}
            />
            <StatCard
              title="SLA 逾期"
              value={data?.slaOverdue ?? 0}
              suffix="件"
              tone={data?.slaOverdue ? 'danger' : 'neutral'}
              icon={<AlertTriangle size={18} />}
            />
            <StatCard
              title="總客戶數"
              value={data?.totalClients ?? 0}
              suffix="家"
              tone="neutral"
              icon={<Users size={18} />}
            />
            <StatCard
              title="有效證書"
              value={data?.activeCertificates ?? 0}
              suffix="張"
              tone="brand"
              icon={<TrendingUp size={18} />}
            />
          </div>

          {/* 圖表區 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: space[4],
            }}
          >
            {/* 月度案件趨勢 BarChart */}
            <SectionCard title="月度案件趨勢">
              {caseTrend.isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: space[6] }}>
                  <Spin />
                </div>
              ) : trendData.length === 0 ? (
                <EmptyState title="暫無趨勢資料" size="sm" />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={trendData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartTooltip />
                    <Legend />
                    <Bar
                      dataKey="created"
                      name="新建案件"
                      fill={color.brand.primary}
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="closed"
                      name="結案"
                      fill={color.state.success}
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </SectionCard>

            {/* 客戶驗證狀態 PieChart */}
            <SectionCard title="客戶驗證狀態分布">
              {clientStats.isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: space[6] }}>
                  <Spin />
                </div>
              ) : verificationDist.length === 0 ? (
                <EmptyState title="暫無客戶資料" size="sm" />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={verificationDist}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={(props) => {
                        const p = props as unknown as { status?: string; percent?: number };
                        const pct = (p.percent ?? 0) * 100;
                        return `${p.status ?? ''} ${pct.toFixed(0)}%`;
                      }}
                      labelLine={false}
                    >
                      {verificationDist.map((_, idx) => (
                        <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartTooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </PageShell>
  );
}
